/** Types mirror Printify API JSON (subset used in UI). */

export type PrintifyShop = {
  id: number
  title: string
}

export type PrintifyShopsResponse = {
  shops: PrintifyShop[]
}

export type PrintifyProductSummary = {
  id: string
  title: string
  visible?: boolean
  images?: Array<{ src?: string; position?: string; is_default?: boolean }>
  /** API may return status string on product or blueprint */
  status?: string
}

export type PrintifyProductsResponse = {
  products: PrintifyProductSummary[]
  currentPage: number
  lastPage: number
  total: number
  perPage: number
}

export type PrintifyProductDetail = Record<string, unknown> & {
  id?: string
  title?: string
  description?: string
  visible?: boolean
  variants?: unknown[]
  images?: Array<{ src?: string; position?: string; is_default?: boolean }>
}

export type PrintifyProductDetailResponse = {
  product: PrintifyProductDetail
}

function printifyErrorMessage(data: unknown, fallback: string): string {
  if (data && typeof data === 'object' && 'message' in data && typeof (data as { message: unknown }).message === 'string') {
    return (data as { message: string }).message
  }
  return fallback
}

export async function getPrintifyShops(brandId: string): Promise<PrintifyShopsResponse> {
  const res = await fetch(`/api/printify/shops?brandId=${encodeURIComponent(brandId)}`)
  const data = await res.json()
  if (!res.ok) throw new Error(printifyErrorMessage(data, `HTTP ${res.status}`))
  return data as PrintifyShopsResponse
}

export async function getPrintifyProducts(
  brandId: string,
  shopId: string,
  page = 1,
  limit = 25,
): Promise<PrintifyProductsResponse> {
  const q = new URLSearchParams({
    brandId,
    shopId,
    page: String(page),
    limit: String(limit),
  })
  const res = await fetch(`/api/printify/products?${q.toString()}`)
  const data = await res.json()
  if (!res.ok) throw new Error(printifyErrorMessage(data, `HTTP ${res.status}`))
  return data as PrintifyProductsResponse
}

export async function getPrintifyProduct(
  brandId: string,
  shopId: string,
  productId: string,
): Promise<PrintifyProductDetailResponse> {
  const q = new URLSearchParams({
    brandId,
    shopId,
    productId,
  })
  const res = await fetch(`/api/printify/product?${q.toString()}`)
  const data = await res.json()
  if (!res.ok) throw new Error(printifyErrorMessage(data, `HTTP ${res.status}`))
  return data as PrintifyProductDetailResponse
}

export function pickProductThumbnail(product: PrintifyProductSummary): string | undefined {
  const imgs = product.images
  if (!imgs?.length) return undefined
  const def = imgs.find((i) => i.is_default)
  const first = imgs[0]
  return (def?.src || first?.src) ?? undefined
}

/** Image entries with a `src`, ordered by `position` for gallery display. */
export type PrintifyProductImage = {
  src?: string
  position?: string | number
  is_default?: boolean
}

/**
 * Returns product images that have a `src`, sorted by `position` (Printify API order).
 */
export function orderPrintifyProductImages(images: PrintifyProductImage[] | undefined): PrintifyProductImage[] {
  if (!images?.length) return []
  const list = images.filter(
    (i): i is PrintifyProductImage & { src: string } => typeof i.src === 'string' && i.src.length > 0,
  )
  list.sort((a, b) => {
    const pa =
      typeof a.position === 'number'
        ? a.position
        : parseInt(String(a.position ?? '0'), 10) || 0
    const pb =
      typeof b.position === 'number'
        ? b.position
        : parseInt(String(b.position ?? '0'), 10) || 0
    return pa - pb
  })
  return list
}
